import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import Popup from 'devextreme-react/popup';
import ScrollView from 'devextreme-react/scroll-view';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import { OPS_SITE_GRID_PROPS } from '../opsDataGrid';
import { showErrorToast, showSuccessToast } from '../notifications/toast';
import { FeedbackDiscussionChat } from './FeedbackDiscussionChat';
import { useFeedbackDiscussion } from './useFeedbackDiscussion';
import type { BugDetail, BugListItem, CommentDetail, CommentListItem, SiteFeedbackApi } from './types';

export interface MyTicketsPageProps {
  api: SiteFeedbackApi;
  currentUserId: string;
  currentUserName?: string;
  initialTab?: 'bugs' | 'feedback';
  initialId?: string | null;
}

export function MyTicketsPage({
  api,
  currentUserId,
  currentUserName,
  initialTab = 'bugs',
  initialId = null
}: MyTicketsPageProps) {
  const [tab, setTab] = useState<'bugs' | 'feedback'>(initialTab);
  const [bugs, setBugs] = useState<BugListItem[]>([]);
  const [feedback, setFeedback] = useState<CommentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [bugDetail, setBugDetail] = useState<BugDetail | null>(null);
  const [feedbackDetail, setFeedbackDetail] = useState<CommentDetail | null>(null);
  const [editMessage, setEditMessage] = useState('');
  const [editSubject, setEditSubject] = useState('');

  const discussionKind = bugDetail ? 'bug' : feedbackDetail ? 'feedback' : null;
  const discussionId = bugDetail?.bugId ?? feedbackDetail?.publicId ?? null;
  const discussion = useFeedbackDiscussion(api, discussionKind, discussionId, Boolean(discussionId));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, f] = await Promise.all([api.listMyBugs(0, 500), api.listMyFeedback(0, 500)]);
      setBugs(b.items);
      setFeedback(f.items);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load your tickets.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!initialId) return;
    if (initialTab === 'bugs') void openBug(initialId);
    else void openFeedback(initialId);
    // intentionally once on mount for deep links
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openBug = useCallback(
    async (bugId: string) => {
      try {
        const d = await api.getMyBug(bugId);
        setFeedbackDetail(null);
        setBugDetail(d);
        setEditMessage(d.message);
        setEditSubject(d.subject);
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to open bug.');
      }
    },
    [api]
  );

  const openFeedback = useCallback(
    async (publicId: string) => {
      try {
        const d = await api.getMyFeedback(publicId);
        setBugDetail(null);
        setFeedbackDetail(d);
        setEditMessage(d.message);
        setEditSubject(d.subject);
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to open feedback.');
      }
    },
    [api]
  );

  const closeDetail = useCallback(() => {
    setBugDetail(null);
    setFeedbackDetail(null);
  }, []);

  const saveLightEdit = useCallback(async () => {
    try {
      if (bugDetail) {
        await api.updateMyBug(bugDetail.bugId, { message: editMessage, subject: editSubject });
        setBugDetail(await api.getMyBug(bugDetail.bugId));
      } else if (feedbackDetail) {
        await api.updateMyFeedback(feedbackDetail.publicId, {
          message: editMessage,
          subject: editSubject
        });
        setFeedbackDetail(await api.getMyFeedback(feedbackDetail.publicId));
      }
      showSuccessToast('Saved.');
      void load();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Save failed.');
    }
  }, [api, bugDetail, editMessage, editSubject, feedbackDetail, load]);

  const toggleWatch = useCallback(async () => {
    try {
      if (bugDetail) {
        const watching = bugDetail.watchers.some(
          (w) => w.userId.toLowerCase() === currentUserId.toLowerCase()
        );
        if (watching) await api.unwatchTicket('bug', bugDetail.bugId);
        else await api.watchTicket('bug', bugDetail.bugId);
        setBugDetail(await api.getMyBug(bugDetail.bugId));
      } else if (feedbackDetail) {
        const watching = feedbackDetail.watchers.some(
          (w) => w.userId.toLowerCase() === currentUserId.toLowerCase()
        );
        if (watching) await api.unwatchTicket('feedback', feedbackDetail.publicId);
        else await api.watchTicket('feedback', feedbackDetail.publicId);
        setFeedbackDetail(await api.getMyFeedback(feedbackDetail.publicId));
      }
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Watch update failed.');
    }
  }, [api, bugDetail, currentUserId, feedbackDetail]);

  const isWatching = useMemo(() => {
    const watchers = bugDetail?.watchers ?? feedbackDetail?.watchers ?? [];
    return watchers.some((w) => w.userId.toLowerCase() === currentUserId.toLowerCase());
  }, [bugDetail, currentUserId, feedbackDetail]);

  const canEdit = useMemo(() => {
    const submittedBy = bugDetail?.submittedBy ?? feedbackDetail?.submittedBy;
    return Boolean(submittedBy && submittedBy.toLowerCase() === currentUserId.toLowerCase());
  }, [bugDetail, currentUserId, feedbackDetail]);

  const detailOpen = Boolean(bugDetail || feedbackDetail);

  return (
    <>
      <ScrollView>
        <div className="ops-page site-support-admin">
          <h2 className="dashboard-title">My Tickets</h2>
          <p className="site-support-hint">
            Bugs and feedback you submitted, watch, or own. You can comment and make light edits; admins
            handle claim, status, and workload.
          </p>

          <TabPanel
            selectedIndex={tab === 'bugs' ? 0 : 1}
            onSelectedIndexChange={(i) => setTab(i === 0 ? 'bugs' : 'feedback')}
            deferRendering={false}
            animationEnabled={false}
            swipeEnabled={false}
          >
            <TabItem title="Bugs">
              <DataGrid
                dataSource={bugs}
                keyExpr="bugId"
                showBorders
                columnAutoWidth
                {...OPS_SITE_GRID_PROPS}
                onRowClick={(e) => {
                  const row = e.data as BugListItem | undefined;
                  if (row?.bugId) void openBug(row.bugId);
                }}
              >
                <Column dataField="bugId" caption="Bug ID" width={140} />
                <Column dataField="subject" caption="Subject" />
                <Column dataField="status" caption="Status" width={120} />
                <Column dataField="severity" caption="Severity" width={100} />
                <Column dataField="assigneeDisplayName" caption="Owner" width={140} />
                <Column dataField="submittedAt" caption="Submitted" dataType="datetime" width={160} />
              </DataGrid>
            </TabItem>
            <TabItem title="Feedback">
              <DataGrid
                dataSource={feedback}
                keyExpr="publicId"
                showBorders
                columnAutoWidth
                {...OPS_SITE_GRID_PROPS}
                onRowClick={(e) => {
                  const row = e.data as CommentListItem | undefined;
                  if (row?.publicId) void openFeedback(row.publicId);
                }}
              >
                <Column dataField="publicId" caption="ID" width={140} />
                <Column dataField="subject" caption="Title" />
                <Column dataField="feedbackType" caption="Type" width={100} />
                <Column dataField="status" caption="Status" width={110} />
                <Column dataField="priority" caption="Priority" width={100} />
                <Column dataField="assigneeDisplayName" caption="Owner" width={140} />
                <Column dataField="submittedAt" caption="Submitted" dataType="datetime" width={160} />
              </DataGrid>
            </TabItem>
          </TabPanel>
          {loading ? <div className="site-support-hint">Loading…</div> : null}
        </div>
      </ScrollView>

      <Popup
        visible={detailOpen}
        onHiding={closeDetail}
        showTitle
        title={bugDetail ? `Bug ${bugDetail.bugId}` : feedbackDetail ? `Feedback ${feedbackDetail.publicId}` : 'Ticket'}
        width={780}
        height="auto"
        maxHeight="92vh"
        hideOnOutsideClick
        wrapperAttr={{ class: 'site-support-bug-popup' }}
        contentRender={() => {
          const d = bugDetail ?? feedbackDetail;
          if (!d) return null;
          return (
            <div className="site-support-popup-body site-support-detail">
              <dl>
                <dt>Status</dt>
                <dd>{d.status}</dd>
                {'severity' in d ? (
                  <>
                    <dt>Severity</dt>
                    <dd>{d.severity}</dd>
                  </>
                ) : (
                  <>
                    <dt>Type</dt>
                    <dd>{(d as CommentDetail).feedbackType}</dd>
                    <dt>Priority</dt>
                    <dd>{(d as CommentDetail).priority}</dd>
                  </>
                )}
                <dt>Owner</dt>
                <dd>{d.assigneeDisplayName ?? d.assigneeUserId ?? 'Unclaimed'}</dd>
                <dt>Watchers</dt>
                <dd>
                  {d.watchers.length === 0
                    ? '—'
                    : d.watchers.map((w) => w.displayName || w.userId).join(', ')}
                </dd>
                <dt>Submitted</dt>
                <dd>{new Date(d.submittedAt).toLocaleString()}</dd>
              </dl>

              {canEdit ? (
                <>
                  <label className="site-support-label">Title</label>
                  <TextBox
                    value={editSubject}
                    onValueChanged={(e) => setEditSubject(String(e.value ?? ''))}
                    maxLength={100}
                  />
                  <label className="site-support-label">Description</label>
                  <TextArea
                    value={editMessage}
                    height={120}
                    onValueChanged={(e) => setEditMessage(String(e.value ?? ''))}
                  />
                </>
              ) : (
                <>
                  <div className="site-support-detail__message">
                    <strong>{'subject' in d ? d.subject : ''}</strong>
                    <div>{'message' in d ? d.message : ''}</div>
                  </div>
                </>
              )}

              <div className="site-support-form-actions">
                <Button
                  text={isWatching ? 'Unwatch' : 'Watch'}
                  stylingMode="outlined"
                  onClick={() => void toggleWatch()}
                />
                {canEdit ? (
                  <Button text="Save edits" type="default" onClick={() => void saveLightEdit()} />
                ) : null}
                <Button text="Close" stylingMode="outlined" onClick={closeDetail} />
              </div>

              <FeedbackDiscussionChat
                messages={discussion.messages}
                currentUserId={currentUserId}
                currentUserName={currentUserName ?? currentUserId}
                loading={discussion.loading}
                onMessageEntered={discussion.post}
                onMessageDeleted={discussion.remove}
              />
            </div>
          );
        }}
      />
    </>
  );
}
